import Box from '@amzn/meridian/box';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { BorderedColumn, GreyBox, PageColumn, WaitFor } from '../components/blocks';
import { Text, useTranslation } from '../components/blocks/localization';
import { ClaimStatusIcon } from '../components/composites';
import { ClaimFilterBar, FilterValues } from '../components/composites/ClaimFilterBar';
import { ClaimReferenceCell } from '../components/composites/ClaimReferenceCell';
import { PaginatedTable } from '../components/constructed';
import { ALL_RESULTS_NUM, CLAIMS_PER_PAGE, MAX_CLAIM_RESULTS } from '../constants';
import { useViewClaimDocument } from '../hooks/useViewClaimDocument';
import {
    claimChangePage,
    claimLoadMoreData,
    claimUpdateFilter,
    claimUpdateSortChange,
    getClaimList,
    getClaimListData,
} from '../redux/actions/claimListActionCreators';
import { getResellerDetails } from '../redux/actions/resellerActionCreators';
import {
    ALL_VALUES,
    ClaimMetadata,
    ClaimSearchValueMap,
    ClaimStatus,
    ClaimTableSortDirection,
    CreditMemoStatus,
    RootState,
    SearchableClaimFields,
    TableData,
} from '../redux/types';
import { IAsynchronouslyLoadedValue } from '../redux/types/asynchronouslyLoadedValue';
import { IAuthorizedReseller } from '../redux/types/reseller';
import { SiteMapPage } from '../types';
import { formatDateIso } from '../utils/dateUtils';
import { useSiteMapRouter } from '../utils/SiteMapRouter';

const initialFilterValues: FilterValues = {
    query: '',
    searchBy: SearchableClaimFields.resellerClaimNumber,
    claimStatusFilter: ALL_VALUES,
    cmStatusFilter: ALL_VALUES,
    dateRange: [],
};

type SortData = {
    sortColumn: string;
    sortDirection: string;
};

const ClaimListPage: FC<ClaimListPageProps> = ({
    reseller,
    resellerDetails,
    claimListState,
    getClaimList,
    getResellerDetails,
    claimChangePage,
    claimLoadMoreData,
    claimUpdateFilter,
    claimUpdateSortChange,
}: ClaimListPageProps) => {
    const [currentReseller, setCurrentReseller] = useState<IAsynchronouslyLoadedValue<IAuthorizedReseller>>();
    const [filterValues, setFilterValues] = useState<FilterValues>(initialFilterValues);
    const { t } = useTranslation('claimsListPage');
    const downloadDocument = useViewClaimDocument();

    const { getPath } = useSiteMapRouter();

    useEffect(
        () => {
            if (
                claimListState.claimListData.status === 'Uninitialized' ||
                currentReseller?.value?.resellerId !== reseller.value?.resellerId
            ) {
                if (reseller.value) {
                    setCurrentReseller(reseller);
                    getClaimList(
                        reseller.value?.resellerId,
                        claimListState.currentPage,
                        MAX_CLAIM_RESULTS,
                        claimListState.sortColumn,
                        claimListState.sortDirection,
                        claimListState.dateRange,
                        claimListState.searchValueMap
                    );
                }
            }
            if (resellerDetails.status === 'Uninitialized' && reseller.value) {
                getResellerDetails(reseller.value.resellerId);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [claimListState, reseller, resellerDetails]
    );

    const getAllData = async (): Promise<TableData<ClaimMetadata, keyof ClaimMetadata>> => {
        if (reseller.value) {
            const data = await getClaimListData(
                reseller.value?.resellerId,
                claimListState.currentPage,
                ALL_RESULTS_NUM,
                claimListState.sortColumn,
                claimListState.sortDirection,
                claimListState.dateRange,
                claimListState.searchValueMap
            );
            return getTableData(data.claimsList);
        }
        const returnOrderData = [] as ClaimMetadata[];
        return getTableData(returnOrderData);
    };

    const onFilterChange = useCallback((field: keyof FilterValues, value: string) => {
        setFilterValues((prev) => ({ ...prev, [field]: value }));
    }, []);

    function setDateRange(dateRange: string[]) {
        setFilterValues((prev) => ({ ...prev, dateRange }));
    }

    const onClearFilters = () => {
        setFilterValues(initialFilterValues);
    };

    const submitQuery = useCallback(
        (searchValueMap: ClaimSearchValueMap) => {
            claimUpdateFilter(searchValueMap, filterValues.dateRange);
        },
        [claimUpdateFilter, filterValues]
    );

    const handleViewClaimDocument = async (uploadId: string) => {
        if (reseller.value?.resellerId) {
            await downloadDocument(reseller.value.resellerId, uploadId);
        }
    };

    const onSort = useCallback(
        (data: SortData) => {
            const { sortColumn, sortDirection } = data;
            claimUpdateSortChange(
                sortColumn,
                ClaimTableSortDirection[sortDirection as keyof typeof ClaimTableSortDirection]
            );
        },
        [claimUpdateSortChange]
    );

    const onPageChange = (value: string) => {
        const intValue = parseInt(value);
        if (intValue) {
            const calcNewStartRecord = CLAIMS_PER_PAGE * (intValue - 1);
            if (
                calcNewStartRecord < claimListState.startAt + MAX_CLAIM_RESULTS &&
                calcNewStartRecord >= claimListState.startAt
            ) {
                claimChangePage(intValue, calcNewStartRecord);
            } else {
                claimLoadMoreData(intValue, calcNewStartRecord);
            }
        }
    };

    const calculateNumberOfPages = (numOfclaim: number | undefined) => {
        if (numOfclaim) {
            return Math.ceil(numOfclaim / CLAIMS_PER_PAGE);
        }
        return 0;
    };

    const getCreditMemoLinkOrStatus = (claim: ClaimMetadata) => {
        if (claim.creditMemoLink) {
            return (
                <ClaimReferenceCell
                    translatedText={t('creditMemo', { context: CreditMemoStatus.COMPLETED })}
                    fileId={claim.creditMemoLink}
                    callback={handleViewClaimDocument}
                />
            );
        } else {
            if (claim.status.toUpperCase() === ClaimStatus.ERRORED) {
                return t('creditMemo_CLAIM_ERRORED');
            }
            return claim.creditMemoStatus ? t('creditMemo', { context: claim.creditMemoStatus }) : '';
        }
    };

    const formatData = (claimList: ClaimMetadata[]) => {
        const startPos = (claimListState.currentPage - 1) * CLAIMS_PER_PAGE - claimListState.startAt;
        const endPos = claimListState.currentPage * CLAIMS_PER_PAGE - claimListState.startAt;

        const pageClaimList: ClaimMetadata[] = claimList.slice(startPos, endPos);

        return getTableData(pageClaimList);
    };

    const getLink = (claim: ClaimMetadata) => {
        return (
            <RouterLink
                to={{
                    pathname: getPath(SiteMapPage.viewClaim, {
                        resellerClaimNumber: encodeURIComponent(claim.resellerClaimNumber),
                    }),
                }}
                style={{ color: '#0073bb', textDecoration: 'none' }}
            >
                {t.cognate(claim.resellerClaimNumber)}
            </RouterLink>
        );
    };

    const getTableData = (claimList: ClaimMetadata[]) => {
        const tableData = new TableData(claimList, [
            {
                title: t('claimNumber-columnTitle'),
                uniqueId: 'resellerClaimNumber',
                format: (claim: ClaimMetadata) => getLink(claim),
            },
            {
                title: t('createdByUser-columnTitle'),
                uniqueId: 'createdByUser',
                format: (claim: ClaimMetadata) => claim.createdByUser,
            },
            {
                title: t('status-columnTitle'),
                uniqueId: 'status',
                format: (claim: ClaimMetadata) => (
                    <ClaimStatusIcon
                        status={claim.status}
                        errors={claim.errorList}
                        key={`${claim.resellerClaimNumber} - ClaimStatusIcon`}
                    />
                ),
            },
            {
                title: t('creationDate-columnTitle'),
                uniqueId: 'creationDate',
                format: (claim: ClaimMetadata) => (
                    <Text key={`${claim.resellerClaimNumber} - creationDateText`}>
                        {t('creationDate-columnValue', { dateTime: formatDateIso(claim.submissionDate) })}
                    </Text>
                ),
            },
            {
                title: t('creditMemoStatus-columnTitle'),
                uniqueId: 'creditMemoLink',
                format: (claim: ClaimMetadata) => getCreditMemoLinkOrStatus(claim),
            },
            {
                title: t('creditMemoNumber-columnTitle'),
                uniqueId: 'creditMemoNumber',
                format: (claim: ClaimMetadata) => claim.creditMemoNumber,
            },
        ]);
        return tableData;
    };

    return (
        <PageColumn width={'100%'}>
            <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
                <GreyBox type={'fill'} spacingInset={'small'} width={'100%'}>
                    <Heading level={5} alignment={'left'} data-testid={'claimListPage.header'}>
                        {t('claimList-pageHeading', { count: claimListState.totalRecords })}
                    </Heading>
                </GreyBox>
                <WaitFor lazyValue={claimListState.claimListData}>
                    {(claimListData) => (
                        <PaginatedTable
                            data={formatData(claimListData)}
                            dataTestId={'claimListTable'}
                            currentPage={claimListState.currentPage}
                            numberOfPages={calculateNumberOfPages(claimListState.totalRecords)}
                            onPageChange={onPageChange}
                            onSort={onSort}
                            sortColumn={claimListState.sortColumn}
                            sortDirection={claimListState.sortDirection}
                            key={`PaginatedTable`}
                            getAllData={getAllData}
                            dataName={t('claimList-pageHeading', { count: claimListState.totalRecords })}
                            rowKey={(rowArray: string[], rowObject: ClaimMetadata) => {
                                return rowObject.resellerClaimNumber;
                            }}
                        >
                            <Box
                                width={'80%'}
                                spacingInset={'none'}
                                data-testid={'claimListPage.filterCriteriaBox'}
                                key={'claimListPage.filterCriteriaBox'}
                            >
                                <Row spacing={'xlarge'} key={'claimListPage.filterCriteriaBox - Row'}>
                                    <ClaimFilterBar
                                        filterValues={filterValues}
                                        onSetDateRange={setDateRange}
                                        onClearFilters={onClearFilters}
                                        onFilterChange={onFilterChange}
                                        submitQuery={submitQuery}
                                    />
                                </Row>
                            </Box>
                        </PaginatedTable>
                    )}
                </WaitFor>
            </BorderedColumn>
        </PageColumn>
    );
};

const mapStateToProps = ({ resellerReducer, resellerClaimListReducer }: RootState) => {
    return {
        reseller: resellerReducer.selectedReseller,
        resellerDetails: resellerReducer.resellerDetails,
        claimListState: resellerClaimListReducer,
    };
};

const mapDispatchToProps = {
    getClaimList,
    claimChangePage,
    claimLoadMoreData,
    claimUpdateFilter,
    claimUpdateSortChange,
    getResellerDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type ClaimListPageProps = ConnectedProps<typeof connector>;
export default connector(ClaimListPage);
